import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "src/ui/shared/Modal/Modal";
import { makeStyles } from "tss-react/mui";

import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Box, DialogProps, Typography } from "@mui/material";
import { useDeleteProductChangeLogMutation } from "@products/api";
import { PermissionService } from "@services/permissionService/permissionService";
import SemverService from "@services/semverService/semverService";
import { CompactIconButton, DangerOutlinedButton } from "@shared/CustomButton";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";

const useStyles = makeStyles()((theme) => ({
    versionBox: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.emteria.border.primary}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.emteria.background.paper,
    },
    topDistance: {
        marginTop: theme.spacing(3),
    },
    changeList: {
        paddingLeft: theme.spacing(2),
        marginTop: theme.spacing(1),
    },
    title: {
        paddingBottom: 0,
    },
    version: {
        fontWeight: "bold",
    },
    versionHeader: {
        position: "relative",
        marginBottom: theme.spacing(1),
    },
    versionTitle: {
        fontWeight: "bold",
    },
    date: {
        position: "absolute",
        top: 0,
        right: 0,
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.spacing(0.5),
        fontWeight: 500,
    },
    dialogActions: {
        justifyContent: "space-between",
        padding: theme.spacing(6),
    },
    dialogActionsFlexEnd: {
        justifyContent: "flex-end",
    },
    deleteButtonBox: {
        position: "relative",
        display: "flex",
        justifyContent: "space-between",
        paddingRight: theme.spacing(5),
    },
    deleteButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(3),
    },
    grow: {
        flexGrow: 1,
    },
}));

interface ChangeLinesProps {
    change: string;
}
const ChangeLines = (props: ChangeLinesProps) => (
    <Box>
        {props.change.split("\n").map((line, lineIndex) => (
            <Typography key={lineIndex} variant="body1">
                {line}
            </Typography>
        ))}
    </Box>
);

export interface ChangesEntry {
    version: string;
    date?: string;
    changes: string[];
}

interface VersionChangesProps {
    version: string;
    date?: string;
    isEditing: boolean;
    changesList: string[];
    productId?: number;
}
const VersionChanges = (props: VersionChangesProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals"]);
    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotDeleteChangelogs = productAbility(workspace).cannot("delete", "Product");
    const hasChanges = props.changesList.length > 0;
    const isDeleteDisabled = cannotDeleteChangelogs || !hasChanges;

    const getDeleteTooltip = () => {
        if (cannotDeleteChangelogs) {
            return t("modals:changesDialog.tooltips.wrongRole");
        }
        if (!hasChanges) {
            return t("modals:changesDialog.tooltips.cannotDeleteNoChangelog");
        }
        return t("modals:changesDialog.tooltips.deleteChangelog");
    };

    const [deleteProductChangeLog] = useDeleteProductChangeLogMutation();

    const handleDelete = async () => {
        if (props.productId) {
            const result = await deleteProductChangeLog({ productId: props.productId, version: props.version });
            if ("error" in result) {
                showErrorToast(getErrorMessage(result.error));
            } else {
                showSuccessToast(t("modals:changesDialog.toast.deletedChangelogSuccess"));
            }
        }
    };

    return (
        <Box key={props.version} className={classes.versionBox}>
            <Box className={classes.deleteButtonBox}>
                <Box className={classes.versionHeader}>
                    <Typography className={classes.versionTitle}>
                        {t("modals:changesDialog.versionLabel", { version: props.version })}
                    </Typography>
                </Box>
                {props.date ? (
                    <Typography variant="caption" className={classes.date}>
                        {props.date}
                    </Typography>
                ) : null}
                {props.isEditing ? (
                    <DangerOutlinedButton
                        onClick={handleDelete}
                        disabled={isDeleteDisabled}
                        // Position the tooltip's wrapper, not the button: a disabled button takes no pointer
                        // events, so hovering it only reaches the wrapper if the wrapper sits under the cursor.
                        tooltipProps={{ title: getDeleteTooltip(), placement: "top", className: classes.deleteButton }}
                    >
                        <i className="fa-solid fa-trash" />
                    </DangerOutlinedButton>
                ) : null}
            </Box>
            <Box className={classes.changeList}>
                {hasChanges ? (
                    props.changesList.map((change, index) => <ChangeLines key={index} change={change} />)
                ) : (
                    <Typography variant="body1">{t("modals:changesDialog.noChanges")}</Typography>
                )}
            </Box>
        </Box>
    );
};

interface ChangesListProps {
    isEditing: boolean;
    changesWithDate: ChangesEntry[];
    productId?: number;
}
const ChangesList: React.FC<ChangesListProps> = (props: ChangesListProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals"]);

    const sortedChanges = [...props.changesWithDate].sort((a, b) => SemverService.compare(b.version, a.version));

    return (
        <Box className={classes.topDistance}>
            {sortedChanges.length > 0 ? (
                sortedChanges.map((versionData) => (
                    <VersionChanges
                        isEditing={props.isEditing}
                        key={versionData.version}
                        version={versionData.version}
                        date={versionData.date}
                        changesList={versionData.changes}
                        productId={props.productId}
                    />
                ))
            ) : (
                <Typography variant="body1">{t("modals:changesDialog.noReleaseNotes")}</Typography>
            )}
        </Box>
    );
};

interface ChangesDialogProps extends Omit<DialogProps, "onClose"> {
    onClose: () => void;
    title: string;
    changesWithDate: ChangesEntry[] | null;
    productId?: number;
}
export const ChangesDialog = ({ productId, changesWithDate, title, ...props }: ChangesDialogProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const { t } = useTranslation(["modals"]);

    const handleEdit = () => {
        setIsEditing((prev) => !prev);
    };
    const handleOnClose = () => {
        props.onClose();
        setIsEditing(false);
    };

    return (
        <Modal
            {...props}
            title={title}
            onClose={handleOnClose}
            titleSuffix={
                productId &&
                changesWithDate &&
                changesWithDate.length > 0 && (
                    <CompactIconButton
                        onClick={handleEdit}
                        aria-pressed={isEditing}
                        tooltipProps={{ title: t("modals:changesDialog.manageVersionChangelogs") }}
                    >
                        <i className="fa-solid fa-gears" />
                    </CompactIconButton>
                )
            }
        >
            <ChangesList isEditing={isEditing} changesWithDate={changesWithDate || []} productId={productId} />
        </Modal>
    );
};
